import { type FC, useState } from 'react'
import { Form, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { SchedulingEmailAudience } from '../../../../models/Scheduling'
import { type SchedulingEmailsTab, type TemplateLookup } from '../../../../models/TemplateLibrary'
import { useZocksAuth } from '../../../../components/ScopedBlock/hooks'
import { schedulingSetupPath } from '../../../Scheduling/shared/bookingNavigation'
import { TemplatesEmptyState, TemplateView } from '../TemplatesTabView'
import { useTemplateCreators } from '../useTemplateCreators'
import { groupSchedulingEmailTemplates } from './schedulingEmails'
import { useSchedulingEmails } from './useSchedulingEmails'

interface AudienceCopy {
  header: string
  subHeader: string
  search: string
}

const AUDIENCE_COPY: Record<SchedulingEmailAudience, AudienceCopy> = {
  [SchedulingEmailAudience.CLIENTS]: {
    header: 'Scheduling emails sent to clients',
    subHeader: 'Templates for emails clients get before and after they book a meeting.',
    search: 'Search emails sent to clients',
  },
  [SchedulingEmailAudience.ADVISOR]: {
    header: 'Scheduling emails sent to team members',
    subHeader: 'Templates for emails team members get when clients book, reschedule or cancel.',
    search: 'Search emails sent to team members',
  },
}

interface SchedulingEmailsTabViewProps {
  tab: SchedulingEmailsTab
  onClick?: (templateLookup: TemplateLookup) => void
}

export const SchedulingEmailsTabView: FC<SchedulingEmailsTabViewProps> = ({ tab, onClick }) => {
  const { hasScope } = useZocksAuth()
  const canManage = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const [search, setSearch] = useState('')
  const { emails, templates, isLoading, isError } = useSchedulingEmails({ refetchOnMountOrArgChange: 60 })
  const usersById = useTemplateCreators(templates)
  const copy = AUDIENCE_COPY[tab.audience]
  const groups = groupSchedulingEmailTemplates(templates ?? [], emails, tab.audience, search)

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start gap-4'>
        <div className='d-flex flex-column gap-1'>
          <h4 className='mb-0'>{copy.header}</h4>
          <div className='paragraph-medium zocks-text-gray-700'>
            {copy.subHeader}{' '}
            {canManage ? (
              <Link to={schedulingSetupPath} state={{ section: 'message-templates' }} className='zocks-text-primary text-decoration-underline'>
                Choose which template each email uses
              </Link>
            ) : null}
          </div>
          {canManage ? null : (
            <div className='paragraph-small zocks-text-gray-600'>Your admin manages scheduling emails for the account. You can view them here.</div>
          )}
        </div>
        <div className='template-search-control d-flex align-items-center gap-2 border rounded zocks-border-color-gray-500 px-2 w-100 flex-shrink-0'>
          <i className='bi bi-search label-large' />
          <Form.Control type='text' value={search} onChange={(e) => setSearch(e.currentTarget.value)} placeholder={copy.search} className='border-0 px-0' />
        </div>
      </div>
      {isLoading ? (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      ) : null}
      {isError ? <div className='paragraph-small zocks-text-gray-600 pt-4'>Could not load the email templates. Please try again.</div> : null}
      {templates && groups.length === 0 ? <TemplatesEmptyState /> : null}
      {groups.length > 0 ? (
        <div className='d-flex flex-column zocks-gap-2 zocks-mt-2.0'>
          {groups.map(({ email, templates: emailTemplates }) => (
            <section key={email.slug} className='d-flex flex-column zocks-gap-0.75' aria-label={email.label}>
              <div>
                <h6 className='paragraph-large fw-bold zocks-text-gray-900 mb-0'>{email.label}</h6>
                <p className='paragraph-small zocks-text-gray-600 mb-0'>{email.description}</p>
              </div>
              <div className='templates-list row row-cols-1 row-cols-md-2 g-3'>
                {emailTemplates.map((template) => (
                  <TemplateView
                    key={template.id}
                    templateLookup={template}
                    showTemplateType={false}
                    createdByUser={'createdBy' in template ? usersById[template.createdBy] : undefined}
                    onClick={onClick}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  )
}
